import { z } from "zod";

/**
 * The drink library (Phase 4 — the moat feature). Contract per the Phase 4
 * spec (authoritative; B4-1 builds the API in parallel from the same
 * contract): Ingredient/Drink shapes, GET /v2/drinks list + CRUD.
 */

export const IngredientCategorySchema = z.enum([
  "spirit",
  "liqueur",
  "mixer",
  "garnish",
  "other",
]);
export type IngredientCategory = z.infer<typeof IngredientCategorySchema>;

export const INGREDIENT_CATEGORIES: IngredientCategory[] = [
  "spirit",
  "liqueur",
  "mixer",
  "garnish",
  "other",
];

export const INGREDIENT_CATEGORY_LABELS: Record<IngredientCategory, string> = {
  spirit: "Spirit",
  liqueur: "Liqueur",
  mixer: "Mixer",
  garnish: "Garnish",
  other: "Other",
};

export const IngredientSchema = z.object({
  name: z.string(),
  // The API stores/returns this as a Decimal, which serializes as a numeric
  // string (e.g. "1.5") rather than a JSON number — z.coerce accepts both so
  // the schema tolerates the wire format without the API changing its type.
  oz_per_serving: z.coerce.number(),
  category: IngredientCategorySchema,
});
export type Ingredient = z.infer<typeof IngredientSchema>;

export const SpiritBaseSchema = z.enum([
  "vodka",
  "gin",
  "rum",
  "tequila",
  "whiskey",
  "brandy",
  "other",
  "none",
]);
export type SpiritBase = z.infer<typeof SpiritBaseSchema>;

export const SPIRIT_BASES: SpiritBase[] = [
  "vodka",
  "gin",
  "rum",
  "tequila",
  "whiskey",
  "brandy",
  "other",
  "none",
];

export const SPIRIT_BASE_LABELS: Record<SpiritBase, string> = {
  vodka: "Vodka",
  gin: "Gin",
  rum: "Rum",
  tequila: "Tequila",
  whiskey: "Whiskey",
  brandy: "Brandy",
  other: "Other",
  none: "None (zero-proof)",
};

export const DrinkStatusSchema = z.enum(["draft", "active", "retired"]);
export type DrinkStatus = z.infer<typeof DrinkStatusSchema>;

export const DRINK_STATUSES: DrinkStatus[] = ["draft", "active", "retired"];

export const DRINK_STATUS_LABELS: Record<DrinkStatus, string> = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
};

export const DRINK_STATUS_BADGE_VARIANT: Record<
  DrinkStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  draft: "secondary",
  active: "success",
  retired: "default",
};

export const DrinkSchema = z.object({
  id: z.string(),
  name: z.string(),
  story: z.string().nullable(),
  ingredients: z.array(IngredientSchema),
  spirit_base: SpiritBaseSchema,
  has_na_variant: z.boolean(),
  na_recipe: z.array(IngredientSchema).nullable(),
  photo_key: z.string().nullable(),
  status: DrinkStatusSchema,
  times_served: z.number(),
  created_at: z.string(),
});
export type Drink = z.infer<typeof DrinkSchema>;

export const DrinkListResponseSchema = z.object({
  items: z.array(DrinkSchema),
  next_cursor: z.string().nullable(),
});
export type DrinkListResponse = z.infer<typeof DrinkListResponseSchema>;

/** A drink is "zero-proof" for the library toggle when it either has an NA
 * twin or carries no spirit at all — matches the Phase 4 spec's toggle
 * definition verbatim (has_na_variant OR spirit_base === 'none'). */
export function isZeroProofEligible(drink: Pick<Drink, "has_na_variant" | "spirit_base">): boolean {
  return drink.has_na_variant || drink.spirit_base === "none";
}

// ---- Mutation inputs ----------------------------------------------------

export interface DrinkInputFields {
  name: string;
  story: string | null;
  ingredients: Ingredient[];
  spirit_base: SpiritBase;
  has_na_variant: boolean;
  na_recipe: Ingredient[] | null;
  photo_key?: string | null;
  status: DrinkStatus;
}

export type CreateDrinkInput = DrinkInputFields;

export interface PatchDrinkInput extends Partial<DrinkInputFields> {
  id: string;
}
